import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import DataTable from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable'
import useFetchPlanningBalances from 'modules/shift_management/components/ShiftManagement/Content/useFetchPlanningBalances'
import useGetShiftsRows from 'modules/shift_management/components/ShiftManagement/MainPage/useGetShiftRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'

const PrintSchedule = () => {
  const navigate = useNavigate()
  const [{ view }] = useSchedule()
  const rootRef = useRef<HTMLDivElement>(null)

  const { shiftsRows: allEmployeesRows, loading: allEmployeesLoading } = useGetShiftsRows({
    printSchedule: true,
  })
  const [{ shiftsRows: contextShiftsRows }] = useShiftsRows()
  const { balancesByEmployee, periodBalancesByEmployee } = useFetchPlanningBalances()
  const hasPrinted = useRef(false)

  const shiftsRows = view.isByEmployees ? allEmployeesRows : contextShiftsRows
  const loading = view.isByEmployees ? allEmployeesLoading : false

  const isDataReady = view.isByEmployees
    ? !loading && allEmployeesRows.length > 0
    : contextShiftsRows.length > 0

  useEffect(() => {
    const handleAfterPrint = () => {
      navigate(-1)
    }

    window.addEventListener('afterprint', handleAfterPrint)

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint)
    }
  }, [navigate])

  useEffect(() => {
    if (isDataReady && !hasPrinted.current) {
      hasPrinted.current = true
      setTimeout(() => {
        // Apply fit-to-page scaling
        if (rootRef.current) {
          const contentWidth = rootRef.current.scrollWidth
          const pageWidth = window.innerWidth
          if (contentWidth > pageWidth) {
            const scale = pageWidth / contentWidth
            rootRef.current.style.transform = `scale(${scale})`
          }
        }
        window.print()
      }, 300)
    }
  }, [isDataReady])

  return (
    <>
      <style>{`
        @media print {
          nav, header, aside, [data-testid="sidebar"] {
            display: none !important;
          }
          .print-schedule-loading {
            display: none !important;
          }
          /* Force layout elements to have visible overflow for print pagination */
          .print-schedule-print-wrapper,
          .print-schedule-root,
          #root,
          body,
          html {
            overflow: visible !important;
          }
          body, html {
            width: auto !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
          }
          body * {
            visibility: hidden !important;
          }
          body,
          #root,
          #root > *,
          .print-schedule-print-wrapper,
          .print-schedule-print-wrapper *,
          .print-schedule-root,
          .print-schedule-root * {
            visibility: visible !important;
          }
          .print-schedule-print-wrapper {
            display: flex !important;
            justify-content: flex-start !important;
            align-items: flex-start !important;
            width: auto !important;
            height: auto !important;
            position: absolute !important;
            left: 0; top: 0;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .print-schedule-header-absolute {
            position: absolute;
            top: 0;
            left: 0;
            height: 32px !important;
            display: flex !important;
            align-items: center;
            padding-left: 8px;
            padding-bottom: 8px;
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            color: #868e96;
          }
          .print-schedule-root {
            transform-origin: top left;
            width: 100% !important;
            height: auto !important;
            position: relative !important;
            overflow: visible !important;
            box-sizing: border-box !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .print-schedule-root [style*='translateY(30%)'] { display: none !important; }
          .print-schedule-root span[role="img"][aria-hidden="true"],
          .print-schedule-root span.relative.flex.rounded-full {
            display: none !important;
          }
          .print-schedule-root img {
            display: none !important;
          }
          .print-schedule-root div[role="checkbox"] {
            display: none !important;
          }
          .print-schedule-root button:last-of-type {
            display: none !important;
          }
          /* Hide delete/bin icons on rest day cells */
          .print-schedule-root [class*="ShiftCellActions_relative"] > [style*="right"] {
            display: none !important;
          }
          /* Ensure shift names truncate instead of overflowing */
          .print-schedule-root [class*="ShiftCellActions_relative"] {
            overflow: hidden !important;
          }
          @page { margin: 0; size: landscape; }
        }
      `}</style>

      <div className="print-schedule-print-wrapper">
        <div className="print-schedule-root" ref={rootRef}>
          <div className="print-schedule-header-absolute">{view.headerTitle}</div>
          <DataTable
            view={view}
            shiftsRows={shiftsRows}
            balancesByEmployee={balancesByEmployee}
            periodBalancesByEmployee={periodBalancesByEmployee}
            disableVirtualization
          />
        </div>
      </div>
    </>
  )
}

export default PrintSchedule
